import harvestService from './harvest';
import { newUuid } from './evaluationQueue';
import type { Bin, StartBinsInput } from '../types/harvest';

/**
 * Bin starts and finishes wait here until the server has them.
 *
 * Orchards have poor coverage. Every start is written to storage with its own
 * `client_uuid`, the time and the fix BEFORE any network call; `flushHarvestQueue`
 * sends them in order and the server is idempotent on `client_uuid`, so a replay
 * starts nothing twice. Offline the phone cannot ask the server for the next
 * field code, so it makes its own — a letter for this device and a counter for
 * today ("K7") — for the picker to chalk; the shed settles any clash.
 */

const KEY = 'factree_harvest_queue_v1';
const DEVICE_KEY = 'factree_harvest_device_letter';
const COUNTER_KEY = 'factree_harvest_offline_counter';

type Queued =
  | { kind: 'start'; input: StartBinsInput }
  | { kind: 'finish'; id: number; fill: number; at: string };

type Listener = () => void;
const listeners = new Set<Listener>();

function read(): Queued[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]') as Queued[]; } catch { return []; }
}
function write(items: Queued[]) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* storage blocked: sent live only */ }
  listeners.forEach(l => l());
}

export function subscribeHarvestQueue(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function pendingHarvestCount(): number {
  return read().length;
}

function deviceLetter(): string {
  try {
    let l = localStorage.getItem(DEVICE_KEY);
    if (!l) {
      l = 'ABCDEFGHJKLMNPQRSTUVWXYZ'[Math.floor(Math.random() * 24)];
      localStorage.setItem(DEVICE_KEY, l);
    }
    return l;
  } catch {
    return 'Z';
  }
}

/** Offline field codes: device letter + today's counter, e.g. "K7". */
export function offlineFieldCodes(count: number, today: string): string[] {
  let state = { date: today, n: 0 };
  try {
    const raw = JSON.parse(localStorage.getItem(COUNTER_KEY) || 'null');
    if (raw && raw.date === today) state = raw;
  } catch { /* fresh counter */ }
  const codes = Array.from({ length: count }, (_, i) => `${deviceLetter()}${state.n + i + 1}`);
  try { localStorage.setItem(COUNTER_KEY, JSON.stringify({ date: today, n: state.n + count })); } catch { /* ignore */ }
  return codes;
}

function isNetworkError(e: unknown): boolean {
  const err = e as { offline?: boolean; status?: number } | null;
  return !!err && (err.offline === true || err.status == null || err.status === 0);
}

/**
 * Start bins now. Returns the bins when the server answered, or `queued: true`
 * with the codes to chalk when it could not be reached.
 */
export async function startBinsQueued(input: Omit<StartBinsInput, 'client_uuid' | 'at'>, today: string): Promise<
  { queued: false; bins: Bin[] } | { queued: true; field_codes: string[] }
> {
  const full: StartBinsInput = { ...input, client_uuid: newUuid(), at: new Date().toISOString() };
  try {
    const r = await harvestService.startBins(full);
    return { queued: false, bins: r.bins };
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    const codes = full.field_codes?.length ? full.field_codes : offlineFieldCodes(full.count ?? 1, today);
    write([...read(), { kind: 'start', input: { ...full, field_codes: codes, count: codes.length } }]);
    return { queued: true, field_codes: codes };
  }
}

export async function finishBinQueued(id: number, fill: number): Promise<Bin | null> {
  const at = new Date().toISOString();
  try {
    return await harvestService.finishBin(id, fill, at);
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    write([...read(), { kind: 'finish', id, fill, at }]);
    return null;
  }
}

/** Send what is waiting. Refused items are dropped and reported; network failures keep them. */
export async function flushHarvestQueue(): Promise<{ sent: number; refused: string[]; offline: boolean }> {
  const items = read();
  let sent = 0;
  const refused: string[] = [];
  const keep: Queued[] = [];
  let offline = false;
  for (const it of items) {
    if (offline) { keep.push(it); continue; }
    try {
      if (it.kind === 'start') await harvestService.startBins(it.input);
      else await harvestService.finishBin(it.id, it.fill, it.at);
      sent++;
    } catch (e) {
      if (isNetworkError(e)) { offline = true; keep.push(it); continue; }
      refused.push((e as Error)?.message || 'Refused by the server');
    }
  }
  write(keep);
  return { sent, refused, offline };
}
